<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import type { Device } from 'src/types/Device';
	import type { CloudRecording, CloudRecordingParams } from '../../types/cloud-recordings';
	import { deleteCloudRecording, getCloudRecordings } from 'src/services/cam-cloud/recordings';

	import RecordingsListItem from './RecordingsListItem.svelte';
	import RecordingsListItemV2 from './RecordingsListItemV2.svelte';
	import RecordingsList from './RecordingsList.svelte';
	import NoResults from './NoResults.svelte';
	import { ensureDeviceKey } from '../../helpers/recording-key-management';
	import { ButtonV2, Datepicker, Loader, toast } from '@Shelly/shelly-design-system/index';
	import { format } from 'svelte-i18n';
	import { capFirstChar } from 'src/helpers/utilities';
	import { dateFormat } from 'src/helpers/date_helper';
	import { getSidepanelTabNavigationContext } from 'src/components/Sidepanel/sidepanel-tab-navigation-context';

	export let device: Device;
	export let selected: CloudRecording = null;

	const isSidepanelContext = !!getSidepanelTabNavigationContext();
	const MAX_LIMIT = 10;
	const dispatch = createEventDispatcher();

	let limit = MAX_LIMIT;
	let offset = 0;
	let noMoreRecordings = false;
	let loadError = false;
	let dateFilter: string = null;
	let datepickerValue: Date[] = [];
	let cloudRecordings: CloudRecording[] = [];
	let isLoading = false;
	let reconciledSelectedId: string = null;
	let seekingSelectedId: string = null;

	$: deviceId = device?.id;
	// `!loadError` stops this empty-list auto-load from retrying forever after a
	// failed request; recovery goes through the explicit retry button instead.
	$: if (deviceId && !isLoading && !noMoreRecordings && !loadError && cloudRecordings.length === 0) {
		offset = 0;
		loadRecordings();
	}
	$: ensureSelectedVisible(selected, cloudRecordings, isLoading);

	const resetPaging = () => {
		cloudRecordings = [];
		offset = 0;
		noMoreRecordings = false;
		loadError = false;
		limit = MAX_LIMIT;
	};

	/**
	 * When the loaded recordings include an encrypted one, make sure a `master_key`
	 * is available up-front so the player never has to stop and ask mid-stream. The
	 * shared, top-level prompt (see `recording-key-management`) is opened only when
	 * a backup exists but no usable password is preserved, and `ensureDeviceKey` is
	 * idempotent (it resolves silently when unlocked and auto-opens the prompt at
	 * most once per session), so paging ("load more") never re-opens the prompt.
	 */
	const ensureRecordingKey = async () => {
		// A guest of a shared camera owns no master key, so there is nothing to unlock.
		if (device?.shared) return;
		const encryptedRecording = cloudRecordings.find((recording) => recording.encrypted);
		if (!encryptedRecording?.mac) return;
		// Scope the up-front unlock to the newest encrypted recording's era so the
		// correct rotation key is resolved/prompted for.
		await ensureDeviceKey(encryptedRecording.mac, encryptedRecording.date);
	};

	// A recording routed in from outside (e.g. the event log) may belong to an
	// older day or a deeper page that isn't loaded yet; jump to its day and keep
	// paging (see loadRecordings) until it shows up in the list.
	const ensureSelectedVisible = (
		recording: CloudRecording,
		list: CloudRecording[],
		loading: boolean,
	) => {
		if (loading || !recording || !deviceId || !list.length) return;
		if (recording.id === reconciledSelectedId) return;

		reconciledSelectedId = recording.id;
		if (list.some((r) => r.id === recording.id)) return;

		// The day filter resolves days in the client's zone, so read the day locally.
		const day = recording.date ? dateFormat(recording.date, 'Y-m-d') : null;
		if (!day) return;

		seekingSelectedId = recording.id;

		if (day !== dateFilter) {
			dateFilter = day;
			datepickerValue = recording.date ? [new Date(recording.date)] : [];
			resetPaging();
			loadRecordings();
		} else if (!noMoreRecordings) {
			offset += MAX_LIMIT;
			loadRecordings();
		} else {
			seekingSelectedId = null;
		}
	};

	const seekSelectedInLoadedPages = () => {
		if (!seekingSelectedId) return;

		const found = cloudRecordings.some((r) => r.id === seekingSelectedId);
		if (found || noMoreRecordings) {
			seekingSelectedId = null;
			return;
		}

		offset += MAX_LIMIT;
		loadRecordings();
	};

	const handleLoadMore = () => {
		if (isLoading || noMoreRecordings) return;
		offset += MAX_LIMIT;
		loadRecordings();
	};

	const handleDateChange = (date) => {
		const newDate = date?.[1];
		if (newDate !== dateFilter) {
			seekingSelectedId = null;
			dateFilter = newDate;
			resetPaging();
			loadRecordings();
		}
	};

	const loadRecordings = () => {
		if (isLoading) return;
		isLoading = true;
		loadError = false;
		const params: CloudRecordingParams = { offset, limit };
		if (dateFilter) {
			params.date = dateFilter;
		}
		getCloudRecordings(deviceId, params)
			.then((response) => {
				isLoading = false;
				limit = response.limit || MAX_LIMIT;
				offset = response.offset || 0;

				// Dedupe by id: paging can overlap (new recordings shift offsets), and a
				// keyed `{#each}` throws on duplicate keys.
				const seenIds = new Set(cloudRecordings.map((r) => r.id));
				const newItems = (response.items as CloudRecording[]).filter((r) => !seenIds.has(r.id));
				cloudRecordings = [...cloudRecordings, ...newItems];

				if (response.offset + response.limit >= response.total) {
					noMoreRecordings = true;
				}

				if (!selected && cloudRecordings.length) {
					handleSelected(cloudRecordings[0]);
				}

				void ensureRecordingKey();
				seekSelectedInLoadedPages();
			})
			.catch((error) => {
				console.log('Failed to load recordings', error);
				isLoading = false;
				loadError = true;
			});
	};

	const handleRetry = () => {
		loadError = false;
		loadRecordings();
	};

	const handleSelected = (recording: CloudRecording) => {
		selected = recording;
		dispatch('select', recording);
	};

	const handleDelete = (recording: CloudRecording) => {
		deleteCloudRecording(recording.id)
			.then(() => {
				toast.open({
					type: 'success',
					title: $format('recording_deleted_success', {
						default: 'Recording deleted successfully',
					}),
				});

				let newSelectedIdx = cloudRecordings.findIndex((r) => r.id === recording.id) - 1;

				cloudRecordings = cloudRecordings.filter((r) => r.id !== recording.id);
				offset = Math.max(0, offset - 1);

				if (selected?.id !== recording.id) {
					return;
				}

				if (newSelectedIdx < 0) {
					newSelectedIdx = 0;
				}

				handleSelected(cloudRecordings?.[newSelectedIdx]);
			})
			.catch(() => {
				toast.open({
					type: 'danger',
					title: $format('recording_deleted_error', {
						default: 'Failed to delete recording',
					}),
				});
			});
	};
</script>

<Datepicker
	aria-label={$format('label_recording_date', { default: 'Recording date' })}
	a11yProps={{
		inputAriaLabel: $format('date_input', { default: 'Date input' }),
		selectMonthAriaLabel: $format('select_month', { default: 'Select month' }),
		selectYearAriaLabel: $format('select_year', { default: 'Select year' }),
	}}
	on:change={(e) => handleDateChange(e.detail)}
	bind:value={datepickerValue}
	id="dp"
	maxDate={new Date()}
	class="mt-m mb-m"
	label={$format('recordings_date_picker', { default: 'Latest Recordings' })}
/>

<RecordingsList on:loadMore={handleLoadMore}>
	{#each cloudRecordings as recording (recording.id)}
		{#if isSidepanelContext}
			<RecordingsListItemV2
				selected={recording?.id === selected?.id}
				{recording}
				on:select={(e) => handleSelected(e.detail)}
				on:delete={(e) => handleDelete(e.detail)}
			/>
		{:else}
			<RecordingsListItem
				selected={recording?.id === selected?.id}
				{recording}
				on:select={(e) => handleSelected(e.detail)}
				on:delete={(e) => handleDelete(e.detail)}
				on:imageClick={(e) => dispatch('imageClick', e.detail)}
			/>
		{/if}
	{/each}
	{#if isLoading}
		<div class="d-flex justify-content-center p-m">
			<Loader />
		</div>
	{:else if loadError && cloudRecordings.length > 0}
		<div class="d-flex justify-content-center p-m">
			<ButtonV2 variant="secondary" size="m" on:click={handleRetry}>
				{capFirstChar($format('button_try_again', { default: 'Try again' }))}
			</ButtonV2>
		</div>
	{:else if !noMoreRecordings && cloudRecordings.length > 0}
		<div class="d-flex justify-content-center p-m">
			<ButtonV2 variant="secondary" size="m" on:click={handleLoadMore}>
				{$format('show_more_btn', { default: 'Show more' })}
			</ButtonV2>
		</div>
	{/if}
</RecordingsList>

{#if !isLoading && !cloudRecordings?.length && (loadError || noMoreRecordings)}
	<div class="mt-l">
		<NoResults
			noResultsFound={loadError
				? {
						icon: 'fa-light fa-triangle-exclamation',
						title: $format('recordings_list_load_error', {
							default: "Couldn't load recordings",
						}),
						description: $format('recordings_list_load_error_description', {
							default: 'Something went wrong. Please try again.',
						}),
					}
				: undefined}
		/>
		{#if loadError}
			<div class="d-flex justify-content-center mt-m">
				<ButtonV2 variant="secondary" size="m" on:click={handleRetry}>
					{capFirstChar($format('button_try_again', { default: 'Try again' }))}
				</ButtonV2>
			</div>
		{/if}
	</div>
{/if}
